import { inject, Injectable, Injector } from '@angular/core';
import { ApiService } from '../api/api.service';
import { preferenceFields, residentFields } from '@resparke/models/resident.model';
import { catchError, concatMap, defer, distinctUntilChanged, EMPTY, exhaustMap, filter, finalize, map, switchMap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { handle as onPhotoUpdateHandle } from './handlers/photos.handler';
import { handle as onResidentUpdateHandle, ResidentSubscriptionResponse } from './handlers/resident.handler';
import { handle as onFacilityHandle } from './handlers/facility.handler';
import { Router } from '@angular/router';
import { AppStateService } from '../app-state/app-state.service';

export type remoteControl = {
  id: string;
  action: string;
  trackId?: string;
}

export interface SubscriptionResponse {
  payload: string;
}

@Injectable({
  providedIn: 'root'
})

export class SubscriptionService {
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly appState = inject(AppStateService);
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);

  private readonly loggedIn$ = this.authService.authState$.pipe(
    filter(Boolean),
    map(authState => authState.isLoggedIn),
    distinctUntilChanged(),
  );

  private activeSubscriptions = new Map<string, boolean>();

  /**
   * @description Initialise subscriptions.
   * NOTE: If [in the near future] we do not do a reload of the browser, we have to make sure the correct
   * subscriptions are loaded when we change modes etc.
   */
  initialise() {
    this.onPhotoUpdate().subscribe();
    this.onResidentUpdate().subscribe();
    this.onFacilityUpdate().subscribe();
  }


  /**
   * @description onPhotoUpdate for Resident and Groups. Condition to be subscribed is an authenticated user of type Device OR AssumedRole
   * and viewMode Group or Resident
   * @returns Observable
   */
  onPhotoUpdate() {
    return this.loggedIn$.pipe(
      switchMap(loggedIn => {
        if (!loggedIn) return EMPTY;
        const source = 'RESIDENT';
        const residentId = this.appState.get('currentResidentId');
        const facilityId = this.appState.get('currentFacilityId');
        const subscriptionKey = `photoUpdate_${source}_${residentId}`;
        if (this.activeSubscriptions.has(subscriptionKey)) {
          return EMPTY;
        }
        return defer(() => {
          this.activeSubscriptions.set(subscriptionKey, true);
          return this.apiCalls.photoUpdate({ id: residentId }).pipe(
            concatMap(response => onPhotoUpdateHandle(this.injector, response, { source, residentId, facilityId }).pipe(
              catchError(err => {
                console.warn('[PHOTO_UPDATE] failed', err);
                return EMPTY;
              })
            )),
            finalize(() => {
              console.log(`[Subscription::Stopped] onPhotoUpdate for id ${residentId}`)
              this.activeSubscriptions.delete(subscriptionKey);
            }),

          )
        });
      }),
    )
  }



  /**
   * @description onResidentUpdate updates a single resident's profile and preference information in AppState. Condition to be
   * subscribed is an authenticated user in Admin or Resident mode.
   * @returns Observable
   */
  onResidentUpdate() {

    return this.loggedIn$.pipe(

      switchMap(isLoggedIn => this.facilityId$().pipe(

        switchMap(facilityId => {
          const subscriptionKey = `residentUpdate_${facilityId}`;
          if (!isLoggedIn || this.activeSubscriptions.has(subscriptionKey)) {
            return EMPTY;
          }
          return defer(() => {
            this.activeSubscriptions.set(subscriptionKey, true);
            return this.apiCalls.residentUpdate({ facilityId }).pipe(
              // switchMap(response => onResidentUpdateHandle(this.injector, response)),
              // try exhaustMap instead of switchmap which should wait for the obs to torn down
              exhaustMap(response => onResidentUpdateHandle(this.injector, response)),
              finalize(() => {
                console.log(`[Subscription::Stopped] onResidentUpdate for facilityId ${facilityId}`)
                this.activeSubscriptions.delete(subscriptionKey);
              })
            );
          });
        }),
      )),
    )
  }

  /**
   * @description onFacilityUpdate subscription should be run when logged in and we have a facilityId.
   * It should be destroyed when logged out or we have no facilityId in authState
   * @returns Observable
   */
  onFacilityUpdate() {
    return this.loggedIn$.pipe(
      switchMap(isLoggedIn => this.facilityId$().pipe(
        switchMap(facilityId => {
          const subscriptionKey = `facilityUpdate_${facilityId}`;
          if (!isLoggedIn || this.activeSubscriptions.has(subscriptionKey)) {
            return EMPTY;
          }
          return defer(() => {
            this.activeSubscriptions.set(subscriptionKey, true);
            return this.apiCalls.facilityUpdate({ id: facilityId }).pipe(
              switchMap(response => onFacilityHandle(this.injector, response)),
              finalize(() => {
                console.log(`[Subscription::Stopped] onFacilityUpdate for id ${facilityId}`)
                this.activeSubscriptions.delete(subscriptionKey);
              })
            );
          });
        }),
      )),
    )
  }

  private apiCalls = {
    facilityUpdate: (variables: { id: string }) => {
      console.log(`[Subscription:Start] onFacilityUpdate for id ${variables.id}`)
      const statement = `subscription onFacilityUpdate($id: ID!) {
        onFacilityUpdate(id: $id) {
          id
          payload
        }
      }`;
      return this.apiService.subscription<SubscriptionResponse>({ statement, variables, type: 'onFacilityUpdate' })
    },

    photoUpdate: (variables: { id: string }, admin = false) => {
      console.log(`[Subscription::Start] on${admin ? 'Admin' : ''}PhotoUpdate for id ${variables.id}`)
      const statement = `subscription onPhotoUpdate($id: ID!) {
        onPhotoUpdate(id: $id) {
          payload
        }
      }`;
      return this.apiService.subscription<SubscriptionResponse>({ statement, variables, type: 'onPhotoUpdate' })
    },
    residentUpdate: (variables: { facilityId: string }) => {
      console.log(`[Subscription::Start] onResidentUpdate for facilityId ${variables.facilityId}`)

      const statement = `subscription onResidentUpdate($facilityId: ID!) {
        onResidentUpdate(facilityId: $facilityId) {
          facilityId
          preference {
            ${preferenceFields}
          }
          resident {
            ${residentFields}
          }
        }
      }`;
      return this.apiService.subscription<ResidentSubscriptionResponse>({ statement, variables, type: 'onResidentUpdate' })
    },
  }


  private facilityId$() {
    return this.appState.get$('currentFacilityId').pipe(
      filter(Boolean),
      distinctUntilChanged()
    );
  }

}
